import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { OutlookSection } from "@/components/OutlookSection";
import { LegacyNotifications } from "@/components/LegacyNotifications";
import { BillingSettingsSection } from "@/components/billing/BillingSettingsSection";
import { V3SettingsSection } from "@/components/V3SettingsSection";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Field, PageHeader } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { useFormState } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  ["address", "Adresse", "N° et rue", "sm:col-span-2"],
  ["postal_code", "Code postal", "42680", ""],
  ["city", "Ville", "Saint-Marcellin-en-Forez", ""],
  ["phone", "Téléphone", "04 00 00 00 00", ""],
  ["email", "Email de contact", "contact@…", ""],
  ["siret", "SIRET", "000 000 000 00000", "sm:col-span-2"],
  ["pickup_address", "Adresse de dépôt / récupération du matériel (communiquée seulement si vous le choisissez)", "N°, rue, code postal, ville", "sm:col-span-2"],
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { form, setForm, set } = useFormState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const o = params.get("outlook");
    if (!o) return;
    if (o === "connected") toast.success("Outlook connecté. Vérifiez l'aperçu avant la première synchronisation.");
    else toast.error(params.get("reason") || "Connexion Outlook impossible");
    setParams({}, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setForm(r.data);
      setLoaded(true);
    });
  }, [setForm]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", form);
      setForm(data);
      toast.success("Coordonnées enregistrées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Paramètres" subtitle="Coordonnées de DTech42 affichées sur les bons d'intervention et les emails clients" />
      <form onSubmit={submit} className="dt-card max-w-3xl space-y-6 p-6" data-testid="company-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label, ph, cls]) => (
            <Field key={k} label={label} className={cls}>
              <Input value={form[k] || ""} onChange={set(k)} placeholder={ph} disabled={!isAdmin || !loaded} data-testid={`company-${k}-input`} />
            </Field>
          ))}
        </div>
        <label className="flex items-start gap-3 text-sm text-slate-200">
          <Switch checked={!!form.show_address_on_docs} onCheckedChange={(v) => setForm({ ...form, show_address_on_docs: v })} disabled={!isAdmin || !loaded} data-testid="company-show-address-switch" />
          <span>Afficher l'adresse complète sur les bons d'intervention et de dépôt<span className="block text-xs text-slate-400">Désactivé : seuls le code postal et la ville apparaissent. Les exports de facturation conservent toujours l'adresse légale et le SIRET.</span></span>
        </label>
        <p className="text-xs text-slate-400">Un champ vide apparaît entre crochets sur le bon d'intervention (ex. « [SIRET DTech42] »).</p>
        {isAdmin ? (
          <Button type="submit" disabled={saving || !loaded} className="dt-glow" data-testid="company-save-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        ) : (
          <p className="text-sm text-slate-400">Seul l'administrateur peut modifier ces informations.</p>
        )}
      </form>
      {isAdmin && <BillingSettingsSection />}
      {isAdmin && <V3SettingsSection />}
      {user?.is_owner && <OutlookSection />}
      {isAdmin && <LegacyNotifications />}
    </>
  );
}
